import { z } from "zod";

import type { DiscountKind } from "@/features/discounts/api/types";
import i18n from "@/i18n";
import { toIsoDate } from "@/shared/lib/iso-date";

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

export const offerSchema = z.object({
	name: z
		.string()
		.trim()
		.superRefine((value, ctx) => {
			if (!value) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: i18n.t("discounts.nameRequired"),
				});
			}
		}),
	type: z.enum(["NATIONAL", "FAMILY", "CORPORATE", "EVERYONE"]),
	promoCode: z.string(),
	isActive: z.boolean(),
	endsOn: z.string().superRefine((value, ctx) => {
		if (value && !CALENDAR_DAY.test(value)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: i18n.t("discounts.endsOnInvalid"),
			});
		}
	}),
	categoryIds: z.array(z.string()),
	partnerIds: z.array(z.string()),
	rates: z.record(z.union([z.number().min(0).max(100), z.literal("")])),
});

export type OfferFormValues = z.infer<typeof offerSchema>;

/**
 * The stored end date of an expired campaign may stay. Any other past day is refused.
 */
export function offerSchemaFor(existingEndsOn = "") {
	const today = toIsoDate(new Date());

	return offerSchema.superRefine((values, ctx) => {
		if (!values.endsOn || values.endsOn === existingEndsOn) {
			return;
		}

		if (values.endsOn < today) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["endsOn"],
				message: i18n.t("discounts.endsOnPast"),
			});
		}
	});
}

export function emptyOfferValues(
	type: DiscountKind = "NATIONAL",
): OfferFormValues {
	return {
		name: "",
		type,
		promoCode: "",
		isActive: true,
		endsOn: "",
		categoryIds: [],
		partnerIds: [],
		rates: {},
	};
}
